import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DefaultLayout } from "../../layouts/Default";
import { Main } from "../../themes/Default/components/Main";
import { Title } from "../../themes/Default/components/Title";
import { TopNavigation } from "../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../themes/Default/components/Breadcrumps";
import { Link } from "../../themes/Default/components/Breadcrumps/components/Link";
import { Button } from "../../themes/Default/components/TopNavigation/components/Button";
import { List } from "../../themes/Default/components/List";
import { Header as ListHeader } from "../../themes/Default/components/List/components/Header";
import { Body as ListBody } from "../../themes/Default/components/List/components/Body";
import { TableHead } from "../../themes/Default/components/List/components/Body/components/TableHead";
import { TableBody } from "../../themes/Default/components/List/components/Body/components/TableBody";
import { TableRow } from "../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableDataCell } from "../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { useTranslation } from "react-i18next";
import { studentGuardianManagement } from "../../hooks/api/StudentGuardianManagement";
import { Pagination } from "../../themes/Default/components/List/components/Footer/components/Pagination";
import { Menu as ListMenu } from "../../themes/Default/components/List/components/Header/components/Menu";
import { Filter as ListFilter } from "../../themes/Default/components/List/components/Header/components/Filter";
import { Button as ListMenuButton } from "../../themes/Default/components/List/components/Header/components/Menu/components/Button";
import { Formik } from "formik";
import { usePersistentFilters } from "../../hooks/core/PersistentFilters";
import { Form } from "../../themes/Default/components/Form";
import { Row } from "../../themes/Default/components/Form/components/Row";
import { Field } from "../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../themes/Default/components/Form/components/Input";
import { getNumberOfUsedFilters } from "../../services/utils/Filter";
import Search from "../../themes/Default/components/List/components/Header/components/Menu/components/Search/Search";

const Guardians = () => {
    const recordsPerPage = 50;
    const defaultFilters = {
        startPage: 0,
        limitPage: recordsPerPage,
        search: ""
    };
    const formRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [filters, setFilters, resetFilters] = usePersistentFilters("guardiansFilters", defaultFilters);
    const [showFilter, setShowFilter] = useState(false);
    const [numberOfUserdFilters, setNumberOfUserdFilters] = useState();
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const [guardians, setGuardians] = useState();
    const { useGuardiansListMutation, useExportGuardiansMutation } = studentGuardianManagement;
    const [guardiansList] = useGuardiansListMutation();
    const [totalRecords, setTotalRecords] = useState(0);
    const [exportGuardians] = useExportGuardiansMutation();

    const gotoEdit = (guardianId) => {
        navigate("/guardians/edit/" + guardianId);
    };

    const openFilter = () => {
        setShowFilter(true);
    };

    const closeFilter = () => {
        setShowFilter(false);
    };

    const onFilter = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onFiltered = (values) => {
        setFilters({
            ...filters,
            firstName: values.firstName,
            lastName: values.lastName,
            phoneNumber: values.phoneNumber,
            email: values.email,
        });
    };

    const onPageChange = (currentPage) => {
        setCurrentPage(currentPage);
        const newStartPage = (currentPage - 1) * recordsPerPage;
        setFilters({
            ...filters,
            startPage: newStartPage,
            limitPage: recordsPerPage,
        });
    };

    const [searchValue, setSearchValue] = useState("");

    const handleSearchChange = (value) => {
        setSearchValue(value);
    };

    const handleSearchSubmit = (value) => {
        setFilters((prev) => ({
            ...prev,
            search: value,
        }));
    };

    const onExport = async () => {
        const blob = await exportGuardians().unwrap();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const today = new Date().toISOString().split('T')[0];
        link.download = `Guardians_${today}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    useEffect(() => {
        guardiansList(filters).then((result) => {
            setGuardians(result?.data?.records);
            setTotalRecords(result?.data?.totalRecords);
        });
        setNumberOfUserdFilters(getNumberOfUsedFilters(filters));
    }, [filters]);

    return (
        <DefaultLayout>
            <TopNavigation>
                <Button title={t("export")} icon="fa-regular fa-cloud-arrow-down" onClick={onExport}></Button>
            </TopNavigation>
            <Main>
                <Title>{`${t("guardians.title")} (${totalRecords})`}</Title>
                <Breadcrumps>
                    <Link title={t("guardians.title")} icon="fa-light fa-clipboard" />
                    <Link title={t("settings.list")} isActive={true} />
                </Breadcrumps>
                    <List>
                        <ListHeader>
                            <ListMenu>
                                <Search
                                    value={searchValue}
                                    onChange={handleSearchChange}
                                    onSearch={handleSearchSubmit}
                                />
                                <ListMenuButton title="Filter" icon="fa-filter" onClick={openFilter} badge={numberOfUserdFilters} />
                            </ListMenu>
                            <ListFilter isVisible={showFilter} onClose={closeFilter} onFilter={onFilter} onResetFilter={resetFilters}>
                                <Formik
                                    innerRef={formRef}
                                    initialValues={{
                                        firstName: filters?.firstName,
                                        lastName: filters?.lastName,
                                        phoneNumber: filters?.phoneNumber,
                                        email: filters?.email,
                                    }}
                                    onSubmit={onFiltered}
                                >
                                    {({ values, handleChange, handleSubmit, resetForm }) => (
                                        <Form>
                                            <Row>
                                                <Field>
                                                    <Input
                                                        name="firstName"
                                                        label={t("students.firstName")}
                                                        value={values.firstName}
                                                        onChange={handleChange}
                                                    />
                                                </Field>
                                            </Row>
                                            <Row>
                                                <Field>
                                                    <Input 
                                                        name="lastName" 
                                                        label={t("students.lastName")} 
                                                        value={values.lastName} 
                                                        onChange={handleChange} 
                                                    />
                                                </Field>
                                            </Row>
                                            <Row>
                                                <Field>
                                                    <Input
                                                        name="email"
                                                        label={t("students.emailAddress")}
                                                        value={values.email}
                                                        onChange={handleChange}
                                                    />
                                                </Field>
                                            </Row>
                                            <Row>
                                                <Field>
                                                    <Input
                                                        name="phoneNumber"
                                                        label={t("students.phoneNumber")}
                                                        value={values.phoneNumber}
                                                        onChange={handleChange}
                                                    />
                                                </Field>
                                            </Row>
                                        </Form>
                                    )}
                                </Formik>
                            </ListFilter>
                        </ListHeader>
                        <ListBody>
                            <TableHead>
                                <TableRow>
                                    <TableHeadCell></TableHeadCell>
                                    <TableHeadCell>
                                        <span>{t("students.firstName")}</span>
                                    </TableHeadCell>
                                    <TableHeadCell>
                                        <span>{t("students.lastName")}</span>
                                    </TableHeadCell>
                                    <TableHeadCell>
                                        <span>{t("students.emailAddress")}</span>
                                    </TableHeadCell>
                                    <TableHeadCell>
                                        <span>{t("students.phoneNumber")}</span>
                                    </TableHeadCell>
                                    <TableHeadCell>
                                        <span>{t("Id card number")}</span>
                                    </TableHeadCell>
                                    <TableHeadCell>
                                        <span>{t("groups.type")}</span>
                                    </TableHeadCell>
                                    <TableHeadCell>
                                        <span>{t("students.username")}</span>
                                    </TableHeadCell>
                                    <TableHeadCell>
                                        <span>{t("Broj dece")}</span>
                                    </TableHeadCell>
                                    <TableHeadCell></TableHeadCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {guardians?.length > 0 &&
                                    guardians?.map((guardian, index) => {
                                        return (
                                            <TableRow key={guardian.id}>
                                                <TableDataCell>
                                                    <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(guardian.id)}></Button>
                                                </TableDataCell>
                                                <TableDataCell>{guardian.firstName}</TableDataCell>
                                                <TableDataCell>{guardian.lastName}</TableDataCell>
                                                <TableDataCell>{guardian.email}</TableDataCell>
                                                <TableDataCell>{guardian.phoneNumber}</TableDataCell>
                                                <TableDataCell>{guardian.idCardNumber}</TableDataCell>
                                                <TableDataCell>{guardian.typeName}</TableDataCell>
                                                <TableDataCell>{guardian.username}</TableDataCell>
                                                <TableDataCell>{guardian.countStudents}</TableDataCell>
                                                <TableDataCell></TableDataCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </ListBody>
                    </List>
                    <Pagination currentPage={currentPage} recordsPerPage={recordsPerPage} totalRecords={totalRecords} onClick={onPageChange} />
            </Main>
        </DefaultLayout>
    );
};


export default Guardians;